import { ExerciseAssetDto } from "@growth-machine-llc/stridist-api-client";
import { Box, IconButton, Theme, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { WorkoutExercise, WorkoutExerciseImage } from "../workout/types";
import { useCurrentWorkspace } from "../../hooks/useCurrentWorkspace";
import { generateThumbnailUrl } from "../../utils/thumbnail";
import logo from "../../components/app/logo.svg";
import { useThumbnail } from "../../hooks/useThumbnail";
import { JourneyAnchors } from "../../constants";
import clsx from "clsx";
import { useUserIsClient } from "../../hooks/useCurrentUser";
import { Eye, PencilLine } from "lucide-react";

type StyleProps = {
  image: string;
  isExerciseImage: boolean;
};

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  exerciseCard: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  image: {
    backgroundImage: ({ image }) => `url("${image}")`,
    backgroundSize: ({ isExerciseImage }) =>
      isExerciseImage ? "cover" : "contain",
    backgroundColor: ({ isExerciseImage }) =>
      isExerciseImage ? "inherit" : theme.palette.background.default,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    width: 60,
    height: 60,
    borderRadius: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(2),
    textAlign: "center",
  },
  titleWithIcon: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
  },
}));

export const getExerciseCover = (
  images?: WorkoutExerciseImage[],
  video?: string,
): { type: "image" | "video"; url: string } | null => {
  if (video) return { type: "video", url: video };

  if (!images?.length) return null;

  const img = images.find((i) => i.cover) ?? images[0];
  if (!img) return null;

  return { type: img.type === "video" ? "video" : "image", url: img.url };
};

interface ExerciseCardProps {
  exercise: ExerciseAssetDto;
  title: string;
  onOpenExerciseDrawer?: () => void;
}

const ExerciseCard = ({
  exercise,
  title,
  onOpenExerciseDrawer,
}: ExerciseCardProps) => {
  const workspace = useCurrentWorkspace();

  const isClient = useUserIsClient();

  const parsedAssetContent =
    exercise?.content && (JSON.parse(exercise.content) as WorkoutExercise);
  const { images, video } = parsedAssetContent ?? {};

  const coverCandidate = getExerciseCover(images, video);

  const videoThumbnail = useThumbnail(
    coverCandidate?.type === "video" ? coverCandidate.url : undefined,
  );

  const exerciseCover =
    coverCandidate?.type === "video" ? videoThumbnail : coverCandidate?.url;

  const defaultLogo =
    generateThumbnailUrl({
      src: workspace?.logoUrl || logo,
      applyModifiers: true,
    }) || logo;

  const s = useStyles({
    image: exerciseCover || defaultLogo,
    isExerciseImage: Boolean(exerciseCover),
  });

  return (
    <Box className={clsx(s.exerciseCard, JourneyAnchors.EXERCISE_CARD)}>
      <Box className={s.image} />
      <Box className={s.titleWithIcon}>
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{
            wordBreak: "break-word",
          }}
        >
          {title}
        </Typography>
        <IconButton
          children={isClient ? <Eye /> : <PencilLine />}
          sx={{
            color: (theme) => theme.palette.text.primary,
          }}
          onClick={onOpenExerciseDrawer}
        />
      </Box>
    </Box>
  );
};

export default ExerciseCard;
